import {
  FluidAnalyticsInterface,
  FluidDataLayerComponentConfiguration,
  FluidFieldValidation,
  FluidFormElementConfig,
  FluidFormElementState,
  FluidFormState,
  FluidIconKey,
  FluidLanguage,
  FluidOption,
  FluidPopoverConfig,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  CancelInput,
  FormLabel,
  Validation,
} from '../../common/fluid-common-form.components';
import { ExpansionToggle } from '../../common/fluid-common-icon.components';
import { INVALID_OPTION_VALUE } from '../../constants/constants';
import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  setI18nState,
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  getFormInputId,
  getValue,
  handleAutoFocus,
  hasGroupedOptions,
  hasValue,
  markTouchedAndValidate,
  reset,
  setValue,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import {
  elementClass,
  setInitialElementState,
} from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';
import {
  FluidDataLayerUtils,
  sendEvent,
} from '../fluid-data-layer/fluid-data-layer.utils';
import {
  FluidSelectTranslationOptions,
  GroupedOptions,
  SingleLevelOptions,
} from './components/fluid-select.components';

/**
 * @displayName Select Field
 *
 * @contextData FLUID Select is a form element that allows users to select an option from a predefined list displayed within a dropdown menu. Select should only be used for lists of 5-15 options to reduce cognitive load & avoid overwhelming users with too many options.
 * <br/><br/>
 * If you have a list of more than 15 options, or need multi-select / autocomplete functionality, use FLUID Combo Box. If your list contains less than 5 options, use FLUID Radio Group.
 *
 * @advancedUsage /forms
 *
 * @builder true
 *
 * @analyticsAware ['selectionCleared', 'selected']
 */
@Component({
  tag: 'fluid-select',
  styleUrl: 'fluid-select.less',
  shadow: true,
})
export class FluidSelect
  implements
    ComponentInterface,
    FluidThemeInterface,
    FluidAnalyticsInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * String that stores the value of initially selected option AFTER
   * validation. Used in "reset" method when "clear" param is false.
   */
  private _validatedInitialValue: string;

  /**
   * Boolean that indicates whether "initializeState()" has been run with a
   * populated "options" array (i.e. options.length > 0). If "options" array
   * is empty/undefined on first render, this Prop will cause "initializeState()"
   * to be run again (instead of "updateState()") when "options" array changes.
   */
  private _stateInitialized = false;

  // ================================================================ //
  // -- Validation Variables

  required: number;

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Reference to the core HTML <select> element.
   */
  _elementRef: HTMLSelectElement;

  /**
   * Boolean that tracks whether the form field has received autofocus
   */
  _autoFocus = false;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidSelectElement;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidSelectTranslationOptions;
  _translateId: string;
  _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;
  @State() _lang: FluidLanguage;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element).
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * An array of FluidOption objects that will populate Select's
   * dropdown menu with options.
   */
  @Prop({ mutable: true }) options: FluidOption[];

  @Watch('options')
  onOptionsChanged(newOpts: FluidOption[], oldOpts: FluidOption[]) {
    switch (true) {
      // If "options" array's CONTENTS haven't changed, terminate
      case Utils.isEqual(newOpts, oldOpts):
        return;
      // If "options" array wasn't populated on first render, re-initialize state
      case !this._stateInitialized && !this._elementState?.value:
        this.initializeState();
        return;
      // Update the selected option (if necessary) given the new "options" array
      default:
        this.updateState();
    }
  }

  /**
   * The value of an option that should be selected by default. NOTE:
   * this Prop is NOT dynamically updated after initial load like the
   * "value" Prop is, & cannot be used to access/update the form field's
   * value after the first render. Alternatively, you can also set the
   * default selected option using FluidOption's "selected" property
   * in the "options" array.
   */
  @Prop({ mutable: true }) initialValue: string;

  /**
   * FLUID Select's current value. This Prop is dynamically updated to
   * reflect the form field's current state, so it can be used as a
   * "synchronous" alternative to the public async "getValue()" method.
   * If provided on first load, populates the form field with a default
   * value. After the component is rendered, changes to this Prop will
   * update the form field's value (similar to "setValue()" method). NOTE:
   * "initialValue" will override this Prop on first load if both Props
   * are provided.
   */
  @Prop({ mutable: true }) value: string;

  @Watch('value')
  onValueChanged() {
    switch (true) {
      // If "value" Prop is equal to "_elementState.value" (or "_stateInitialized" is false), terminate
      case this.valueEqualsState():
        return;
      // If "value" Prop is null, undefined, or '', reset the form field
      case isNullOrUndefined(this.value, true):
        this.reset(true);
        break;
      // Otherwise, validate that value is associated with an existing option & update "_elementState.value"
      default:
        this.touched = false;
        this._elementState = {
          ...this._elementState,
          value: this.validateValue(this.value, false, true),
          touched: this.touched,
        };
    }
    this.validate().then();
  }

  /**
   * When provided, a FLUID Form Label will be rendered above the Select
   * with the specified string. NOTE: it is best practice for accessibility
   * to provide a label.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default, overflowing
   * label text will be truncated with ellipsis.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow;

  /**
   * When provided, a supplementary muted label will be displayed after
   * the primary Form Label.
   */
  @Prop({ mutable: true }) supplementaryLabel: string;

  /**
   * When true, Select's embedded <select> will be disabled.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * When true, Select's embedded <select> will be in a "readonly" state.
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * An array of field validation options (see "Validation" & "Custom
   * Validation" tabs within the "Basic Forms Walkthrough" guide in the
   * FLUID Documentation Site for more information).
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  /**
   * Placeholder text to display when no option is selected.
   */
  @Prop({ mutable: true }) placeholder = 'Choose Option';

  /**
   * When true, removes the form field's default bottom margin.
   */
  @Prop({ mutable: true }) inlineField: boolean;

  /**
   * When true, displays a "clear" button that resets the form field when
   * clicked. An option must be selected for the button to be visible.
   * Button is NOT displayed when field has "required" validation.
   */
  @Prop({ mutable: true }) allowCancel: boolean;

  /**
   * When provided, Select will render a "helptext" icon with attached
   * Popover adjacent to its label. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * When true, this field will automatically have focus on page load.
   * Note: only one form field should have "autoFocus" set to true
   * on a page. Multiple fields with "autoFocus: true" can/will cause
   * unexpected behavior.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * Allows Select's Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidFormElementConfig;

  @Watch('config')
  onConfigChanged() {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
  }

  // ================================================================ //
  // -- Events

  /**
   * Emits the FluidOption value of the selected option
   * accessible from $event.detail
   */
  @Event() selected: EventEmitter<FluidOption>;

  /**
   * Emits whenever the selection was cleared with the cancel
   * icon - { payload: FluidOption } - the option that was cleared.
   */
  @Event() selectionCleared: EventEmitter<FluidOption>;

  /**
   * Emits the entire element state for the element
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: selectiveMapConfigToLocal - set local Props to "config" values
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
    Utils.setGlobalTheme(this);
    this.setupAnalytics();
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.initializeState();
    this._autoFocus = this.autoFocus;
  }

  /**
   * Called AFTER every render().
   */
  componentDidRender() {
    handleAutoFocus(this);
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * @action: updateValue - dynamically update the "value" Prop to match "_elementState.value"
   */
  componentWillUpdate() {
    this.updateValue();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async setValue(value: any, opts?: Record<string, any>) {
    return setValue(
      this,
      isNullOrUndefined(value)
        ? value // If value is undefined, pass along to setValue() so reset() is still called
        : this.validateValue(value, false, true),
      null,
      opts
    );
  }

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    return reset(this, clear, () => {
      if (clear) return;
      this._elementState = setInitialElementState(
        this._validatedInitialValue, // Ensures value exists within "options" array
        this.controlName
      );
    });
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  @Method() async getNativeInput() {
    return this._elementRef;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Returns true if "value" Prop equals "_elementState.value" OR if
   * "options" Prop has not been populated yet (i.e. is undefined) -
   * otherwise, returns false. Used to determine if "value" Prop is
   * out-of-sync with internal state.
   */
  valueEqualsState(): boolean {
    return !this._stateInitialized || this.value === this._elementState?.value;
  }

  /**
   * Dynamically updates the "value" Prop to match "_elementState.value".
   * Called every time the component updates/re-renders.
   */
  updateValue(): void {
    if (this.valueEqualsState()) return;
    this.value = this._elementState.value;
  }

  /**
   * Determines Select's default value (if provided), sets up the
   * form field's internal state, & triggers update to "value" Prop.
   */
  initializeState(): void {
    this._stateInitialized = !!this.options?.length;

    if (
      isNullOrUndefined(this.initialValue) &&
      isNullOrUndefined(this.value, true)
    ) {
      this._validatedInitialValue = this.getSelectedOptionValue();
    } else {
      this._validatedInitialValue = this.validateValue(
        this.initialValue ?? this.value,
        false,
        this._stateInitialized
      );
    }

    this._elementState = setInitialElementState(
      this._validatedInitialValue,
      this.controlName
    );

    this.updateValue();
  }

  /**
   * Updates Select's internal state when "options" Prop changes.
   * Sets selected option from the new "options" array OR attempts to
   * retain the previously selected option IF it exists in the new
   * "options" array AND its "selected" property is NOT explicitly set
   * to "false".
   */
  updateState(): void {
    if (isNullOrUndefined(this._elementState)) return;

    this._elementState.value =
      this.getSelectedOptionValue() ||
      this.validateValue(this._elementState.value, true, false);
  }

  /**
   * Validates that the provided value exists within "options" array.
   * Optionally, also checks that "selected" property of specified value
   * is NOT explicitly false.
   * @param value - the value to validate
   * @param checkSelected - when true, value will be considered invalid if "selected: false"
   * @param invalidWarning - whether to display a console warning message
   * @return - the validated value OR empty string (if value does NOT pass validation)
   */
  validateValue(
    value: any,
    checkSelected?: boolean,
    invalidWarning?: boolean
  ): string {
    const matchedOpt = this.options?.find(
      (opt) =>
        `${opt.value}` === `${value}` &&
        (checkSelected ? opt.selected !== false : true)
    );

    switch (true) {
      case isNullOrUndefined(value) || !this.options?.length:
        return '';
      case !matchedOpt:
        invalidWarning &&
          Utils.consoleWarn(this.host, INVALID_OPTION_VALUE(value));
        return '';
      default:
        return `${value ?? ''}`;
    }
  }

  /**
   * Finds & returns the "value" property of the FluidOption with
   * "selected: true" in the "options" array (if one exists). Returns
   * empty string if "options" array doesn't contain a selected option or
   * if the "options" array is undefined.
   */
  getSelectedOptionValue(): string {
    return `${this.options?.find((opt) => opt.selected)?.value ?? ''}`;
  }

  emitSelectedValue(): void {
    const selectedOption = this.options?.find(
      (option: FluidOption) => option.value === this._elementState.value
    );
    this.selected.emit(selectedOption);
    sendEvent(this.host, this.parentLayerId, 'selected', selectedOption);
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const styles = elementClass(
      'fluid-select',
      this.theme,
      this._elementState,
      this.inlineField,
      this.touched,
      {
        disabled: this.disabled,
        readonly: this.readonly,
        'allow-cancel': this.allowCancel,
        'has-value': hasValue(this._elementState?.value),
      }
    );

    return (
      <div class={styles.componentWrapper}>
        <div
          part={this._enableParts && 'fluid-select form-group'}
          class={styles.formGroupClass}>
          <FormLabel host={this} />
          <div class={'input-wrapper'}>
            <select
              part={this._enableParts && 'fluid-select select'}
              class={styles.inputClass}
              id={getFormInputId(this.config?.id, this.controlName)}
              ref={(el) => (this._elementRef = el)}
              disabled={this.disabled || this.readonly}
              name={this._elementState.name}
              onBlur={() => this.markTouchedAndValidate()}
              onChange={(event: InputEvent) =>
                update(this, event, this.emitSelectedValue.bind(this))
              }>
              <option
                disabled={true}
                ref={(el) => (el.selected = !this._elementState.value)}>
                {translateProperty.bind(this)('placeholder')}
              </option>
              {!hasGroupedOptions(this.options) ? (
                <SingleLevelOptions
                  options={this.options}
                  store={this._i18nStore}
                  fluidTranslate={this.fluidTranslate}
                  elementState={this._elementState}
                />
              ) : (
                <GroupedOptions
                  options={this.options}
                  store={this._i18nStore}
                  fluidTranslate={this.fluidTranslate}
                  elementState={this._elementState}
                />
              )}
            </select>
            <CancelInput
              host={this}
              onCancel={(preCancelValue) => {
                const cancelledOption = this.options.find(
                  (opt) => opt.value === preCancelValue
                );
                this.selectionCleared.emit(cancelledOption);
                sendEvent(
                  this.host,
                  this.parentLayerId,
                  'selectionCleared',
                  cancelledOption
                );
                this.emitSelectedValue.bind(this);
              }}
            />
            <ExpansionToggle
              class='dropdown-icon'
              iconKey={this._toggleIconKey}
              open={false}
              disabled={this.disabled || this.readonly}
              theme={this.theme}
              doRender={true}
            />
          </div>
          <div
            class={{
              'form-element-notifications': true,
              'inline-field': this.inlineField,
            }}>
            <Validation host={this} fluidTranslate={this.fluidTranslate} />
          </div>
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * If the element should be configured at a component level for the FLUID data layer,
   * this configuration dictates how it should be instrumented
   * @internal
   * @experimental
   */
  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(this.host)?.id;
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;

  /**
   * Enables "part" attributes on certain elements. IMPORTANT: this
   * Prop should only be set to "true" when the component is being used
   * within another FLUID component.
   * @internal
   */
  @Prop({ mutable: true }) _enableParts: boolean;

  /**
   * @internal
   */
  @Prop({ mutable: true }) _toggleIconKey: FluidIconKey;
}
